'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { MARKET_FEED_PATHS, readPublicFeed } from '@/lib/marketFeedsApi';
import { marketRowDisplay } from '@/lib/marketRows';
import { startMarketRowsPoller } from '@/lib/marketRowsPoller';
import type { MarketRowsView } from '@/lib/marketRowsPoller';
import { cssVars } from '@/lib/style';
import { AppIcon } from '@/components/ui/AppIcon';
import { MARKET_ROWS } from './homeData';

const MarketRowsContext = createContext<MarketRowsView | null>(null);

interface MarketRowsProviderProps {
  /** Server-rendered (ISR) row state and status; the browser keeps it current. */
  initial: MarketRowsView;
  children: ReactNode;
}

/** Shares one refresh loop between the market rows and the footer status line. */
export function MarketRowsProvider({ initial, children }: MarketRowsProviderProps) {
  const [view, setView] = useState<MarketRowsView>(initial);

  useEffect(() => {
    const poller = startMarketRowsPoller(initial.state, {
      read: (signal) => readPublicFeed(MARKET_FEED_PATHS.terminal, { signal }),
      onChange: setView,
    });
    const onVisibility = () => poller.visibility();
    document.addEventListener('visibilitychange', onVisibility);
    void poller.refresh();
    return () => {
      poller.stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // The loop starts once per mount from the server state; later state lives in the poller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <MarketRowsContext.Provider value={view}>{children}</MarketRowsContext.Provider>;
}

function useMarketRows(): MarketRowsView {
  const view = useContext(MarketRowsContext);
  if (!view) throw new Error('useMarketRows must be used inside MarketRowsProvider');
  return view;
}

/** "Across the markets" rows: value + reporting period, or the awaiting state. */
export function HomeMarketRows() {
  const { state } = useMarketRows();
  return (
    <>
      {MARKET_ROWS.map((row) => {
        const display = marketRowDisplay(state[row.key]);
        return (
          <div
            key={row.key}
            className="cid-market-row"
            style={cssVars({ '--market-accent': row.accent })}
          >
            <div className="cid-market-symbol">
              <AppIcon name={row.icon} />
            </div>
            <div>
              <h4>{row.country}</h4>
              <p>{row.measure}</p>
              <small>{display.period}</small>
            </div>
            <div className="cid-market-number">
              <span>{display.value}</span> <small>{row.unit}</small>
            </div>
          </div>
        );
      })}
    </>
  );
}

/** Footer live-region text for the market rows. */
export function MarketRowsStatus() {
  return <>{useMarketRows().status}</>;
}
